import { supabase } from "@/shared/lib/supabase";
import { extractTextFromPdf } from "@/shared/utils/pdfTextExtraction";
import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";
import { DocumentLimitError } from "@/shared/utils/uploadDocument";
export async function uploadSupportingDocument(
  file: File,
  estateId: number,
  accessToken: string,
  documentType: string,
): Promise<number> {
  let fileForUpload = file;
  let fileName = file.name;
  let contentType = file.type;

  if (contentType.startsWith("image/")) {
    const converted = await convertImageFileToPdf(file);
    fileForUpload = converted;
    fileName = converted.name;
    contentType = "application/pdf";
  }

  const extractedText =
    contentType === "application/pdf" ? await extractTextFromPdf(fileForUpload) : undefined;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  const presignedRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/presigned-url`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ file_name: fileName }),
    },
  );
  if (!presignedRes.ok) {
    if (presignedRes.status === 403) throw new DocumentLimitError();
    throw new Error("Failed to get upload URL");
  }
  const { upload_url, file_key } = await presignedRes.json();

  const uploadRes = await fetch(upload_url, {
    method: "PUT",
    body: fileForUpload,
    headers: { "Content-Type": contentType },
  });
  if (!uploadRes.ok) throw new Error("Failed to upload file");

  const metadataRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/metadata`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        file_key,
        file_name: fileName,
        document_type: documentType,
        extracted_text: extractedText,
      }),
    },
  );
  if (!metadataRes.ok) {
    if (metadataRes.status === 403) throw new DocumentLimitError();
    throw new Error("Failed to save document");
  }
  const { document } = await metadataRes.json();

  return document.id as number;
}

export async function uploadDocumentWithAuth(
  file: File,
  estateId: number,
  documentType: string,
): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");
  return uploadSupportingDocument(file, estateId, session.access_token, documentType);
}
